/**
 * companies/{companyId}/studios コレクションの購読・作成・更新。
 * #2133 設計 §4: スタジオ(company) 一覧・作成・編集。
 * packages/common StudioUsecases.create/update と同じく、ドキュメント全体を
 * set()/update() する(company 専用の操作)。
 */
import { collection, doc, getDoc, getDocs, onSnapshot, setDoc, updateDoc, type Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './client';
import { logger } from '../logger';
import type { Studio } from '@/types/firestore';

function studiosCollectionPath(companyId: string): string {
  return `companies/${companyId}/studios`;
}

export function subscribeToStudios(
  companyId: string,
  onChange: (studios: Studio[]) => void,
  onError: (error: unknown) => void
): Unsubscribe {
  if (!db) {
    logger.error('Firestore not initialized');
    onError(new Error('Firestore not initialized'));
    return () => {};
  }

  return onSnapshot(
    collection(db, studiosCollectionPath(companyId)),
    (snapshot) => {
      onChange(snapshot.docs.map((d) => d.data() as Studio));
    },
    (error) => {
      logger.error('Failed to subscribe to studios', error);
      onError(error);
    }
  );
}

/**
 * #2133 設計第3弾 §3 項目1: 会社のポーズ削除時の使用数カウント
 * (companyPoseUsage.ts)が、会社に属するスタジオの一覧を一度だけ取得するために
 * 使う(購読ではなく1回読み)。
 */
export async function fetchStudios(companyId: string): Promise<Studio[]> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  const snapshot = await getDocs(collection(db, studiosCollectionPath(companyId)));
  return snapshot.docs.map((d) => d.data() as Studio);
}

export async function fetchStudio(companyId: string, studioId: string): Promise<Studio | null> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  const snapshot = await getDoc(doc(db, studiosCollectionPath(companyId), studioId));
  return snapshot.exists() ? (snapshot.data() as Studio) : null;
}

export async function createStudio(companyId: string, studio: Studio): Promise<void> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  await setDoc(doc(db, studiosCollectionPath(companyId), studio.id), studio);
}

export async function updateStudio(companyId: string, studio: Studio): Promise<void> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  await updateDoc(doc(db, studiosCollectionPath(companyId), studio.id), {
    ...studio,
  } as Record<string, unknown>);
}

/** deleteStudioが失敗(failed-precondition)した際に、サーバーが返す機械判定可能な理由(#2285)。 */
export type DeleteStudioBlockerReason =
  | 'has_users'
  | 'has_temp_users'
  | 'has_lessons'
  | 'has_mats'
  | 'has_own_content';

export class DeleteStudioBlockedError extends Error {
  constructor(public readonly reason: DeleteStudioBlockerReason | null) {
    super(`Cannot delete studio: ${reason ?? 'unknown'}`);
    this.name = 'DeleteStudioBlockedError';
  }
}

/**
 * スタジオを削除する(#2285)。firestore.rulesはstudioAssociate/instructor/
 * studentからの削除を開いていない(design.md §4)ため、Admin SDK経由の
 * callableに閉じる。中に人・レッスン・固有コンテンツが残る場合は
 * failed-preconditionで拒否され、details.reasonにDeleteStudioBlockerReasonが
 * 入る。それをDeleteStudioBlockedErrorへ変換し、呼び出し側(StudioList)が
 * 理由ごとの日本語案内文を出せるようにする。
 */
export async function deleteStudio(companyId: string, studioId: string): Promise<void> {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    await httpsCallable(functions, 'studios-deleteStudio')({ companyId, studioId });
  } catch (error: unknown) {
    const details = (error as { details?: unknown } | null)?.details;
    const reason =
      details && typeof details === 'object' && 'reason' in details
        ? ((details as { reason: unknown }).reason as DeleteStudioBlockerReason)
        : null;
    const code = (error as { code?: string } | null)?.code;
    if (code === 'functions/failed-precondition' && reason) {
      throw new DeleteStudioBlockedError(reason);
    }
    throw error;
  }
}
